import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Подключаем плагины dayjs для работы с часовыми поясами
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Получает часовой пояс пользователя из браузера
 * @returns {string} IANA timezone identifier (например, 'Europe/Moscow')
 */
export function getUserTimezone() {
    try {
        // Пытаемся получить часовой пояс из браузера
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (browserTimezone) {
            return browserTimezone;
        }
    } catch (error) {
        console.warn('Не удалось определить часовой пояс браузера:', error);
    }
    
    // Fallback на московское время (по умолчанию для российских пользователей)
    return 'Europe/Moscow';
}

/**
 * Конвертирует UTC время в локальный часовой пояс пользователя
 * @param {string|Date|dayjs.Dayjs} utcTime - UTC время (строка ISO, Date или dayjs объект)
 * @param {string} [userTimezone] - Часовой пояс пользователя (если не указан, определяется автоматически)
 * @returns {dayjs.Dayjs} dayjs объект в локальном часовом поясе
 */
export function convertToUserTimezone(utcTime, userTimezone = null) {
    const tz = userTimezone || getUserTimezone();
    
    // Парсим время как UTC
    const utcDate = dayjs.utc(utcTime);
    
    // Конвертируем в локальный часовой пояс
    return utcDate.tz(tz);
}

/**
 * Форматирует время в локальном часовом поясе пользователя
 * @param {string|Date|dayjs.Dayjs} utcTime - UTC время
 * @param {string} format - Формат dayjs (по умолчанию 'HH:mm')
 * @param {string} [userTimezone] - Часовой пояс пользователя
 * @returns {string} Отформатированное время
 */
export function formatTimeInUserTimezone(utcTime, format = 'HH:mm', userTimezone = null) {
    const localTime = convertToUserTimezone(utcTime, userTimezone);
    return localTime.format(format);
}

/**
 * Форматирует дату в локальном часовом поясе пользователя
 * @param {string|Date|dayjs.Dayjs} utcTime - UTC время
 * @param {string} format - Формат dayjs (по умолчанию 'DD.MM.YYYY')
 * @param {string} [userTimezone] - Часовой пояс пользователя
 * @returns {string} Отформатированная дата
 */
export function formatDateInUserTimezone(utcTime, format = 'DD.MM.YYYY', userTimezone = null) {
    const localTime = convertToUserTimezone(utcTime, userTimezone);
    return localTime.format(format);
}

/**
 * Форматирует дату и время в локальном часовом поясе пользователя
 * @param {string|Date|dayjs.Dayjs} utcTime - UTC время
 * @param {string} [dateFormat] - Формат даты (по умолчанию 'DD.MM.YYYY')
 * @param {string} [timeFormat] - Формат времени (по умолчанию 'HH:mm')
 * @param {string} [userTimezone] - Часовой пояс пользователя
 * @returns {object} Объект с полями date и time
 */
export function formatDateTimeInUserTimezone(utcTime, dateFormat = 'DD.MM.YYYY', timeFormat = 'HH:mm', userTimezone = null) {
    const localTime = convertToUserTimezone(utcTime, userTimezone);
    return {
        date: localTime.format(dateFormat),
        time: localTime.format(timeFormat),
        datetime: localTime,
        full: localTime.format(`${dateFormat} ${timeFormat}`)
    };
}
